// Turns the rendered report table into a preview of the columns being composed
// in the inline column editor: the report's own data columns are collapsed away
// (the row-label column stays) and cells standing for the configured columns are
// appended in their place. Headers only — no figures are rendered here, they are
// computed when the columns are applied.

import Tooltip from '@app/setup/tooltip/uikit-tooltip'

import type { PreviewColumn, PreviewHeader } from './types'

import { useColumnEditorI18n } from './use-column-editor-i18n'

// Set on the table while it is previewing. The report's own columns are hidden
// from a single stylesheet rule keyed off this class (see reports.scss) rather
// than cell by cell: a large report is tens of thousands of cells, and toggling
// one class leaves the work to the browser.
const PREVIEWING_CLASS = 'report-column-preview'
const CELL_CLASS = 'report-column-preview__cell'

// Set while a preview is on its way: the report's own figures go, headers aside,
// and go out of the layout with it. A column is as wide as the widest thing in
// it, so with the figures gone the table already measures what it will measure
// once the preview is in — see reports.scss. The columns settle as the drawer
// opens, and the preview landing moves nothing.
const PENDING_CLASS = 'report-column-preview-pending'

// A period the report would drop, previewed greyed out.
const HIDDEN_CELL_CLASS = 'report-column-preview__cell--hidden'

// The row the preview brings for the value labels. Marks it as the preview's to
// take away again, and keeps it out of the rule that hides the report's own
// subheader rows (see reports.scss).
const PREVIEW_ROW_CLASS = 'report-column-preview__row'

// The report's own stacking classes, mirroring `report_table_cell` in
// reports_helper.rb: header cells are sticky and have to keep painting over the
// body cells scrolling under them.
const HEADER_CLASS = 'report__table--level-3'
const BODY_CLASS = 'report__table--level-1'

// What the report renders a spacer column with — a blank column without the
// borders that separate real ones.
const SPACER_CLASS = 'report-spacer'

// One period of one configured column: the date, and what the columns under it
// show. The report renders `max(1, sublabels.length)` columns for it.
//
// What the server said about the period plus the one thing this module adds — a
// spacer is a column with no period behind it. Intersected rather than restated,
// so a field added to PreviewHeader has to be accounted for here instead of
// being dropped on the way through `flatten`.
type PreviewGroup = PreviewHeader & { spacer: boolean }

// Tooltips are attached to the header cells of dropped periods. They own DOM of
// their own outside the table, so they are disposed when the cells go rather
// than left pointing at detached elements.
let tooltips: Tooltip[] = []

export const findReportTable = (): HTMLTableElement | null =>
  document.querySelector<HTMLTableElement>('.report-table-container table')

// Whether this table is currently showing a preview instead of its own columns.
// Asked of the table rather than remembered by the caller: a preview belongs to
// the table it was rendered into, and the table is replaced out from under the
// column editor often enough (the frame reloads on a period set change, on a
// finished async render) that a flag beside it is a second answer to keep in
// step with this one.
export const isPreviewing = (table: HTMLTableElement): boolean =>
  table.classList.contains(PREVIEWING_CLASS)

// Empties the report's own figures for as long as it takes the first preview to
// arrive, leaving the table laid out the way the preview will find it. The headers
// keep naming their periods until the preview's own headers replace them.
//
// Takes a missing table for the same reason `restoreReportColumns` does: the
// table can be gone (the frame reloads and replaces it), and there is nothing to
// empty when it is.
export const blankReportColumns = (table: HTMLTableElement | null): void => {
  table?.classList.add(PENDING_CLASS)
}

// Hides the report's own columns. Only ever done together with putting preview
// cells in their place, so the table is never left empty: it holds its real
// columns until there is something to show instead of them.
const collapseReportColumns = (table: HTMLTableElement): void => {
  table.classList.remove(PENDING_CLASS)
  table.classList.add(PREVIEWING_CLASS)
}

// Takes a missing table: the tooltips are this module's to clean up either way,
// and the table can be gone by the time the drawer closes (the frame reloads and
// replaces it).
export const restoreReportColumns = (table: HTMLTableElement | null): void => {
  disposeTooltips()
  if (!table) return

  table.classList.remove(PREVIEWING_CLASS, PENDING_CLASS)
  removePreviewCells(table)
}

export const renderPreviewColumns = (
  table: HTMLTableElement,
  columns: PreviewColumn[]
): void => {
  // Always there: the report's data matrix pushes its header row before anything
  // else (Report::TemplateLogic#data_matrix). A table without one is a bug worth
  // hearing about, so this throws rather than bailing out — the caller gives the
  // report its own columns back and reports it.
  //
  // Thrown before the table is touched, or that recovery has nothing to work
  // with: the caller only restores a table it hasn't already marked as
  // previewing, and a half-collapsed table would keep the report's own columns
  // hidden with no preview cells to show in their place.
  const headerRow = table.querySelector<HTMLTableRowElement>('thead tr')
  if (!headerRow) throw new Error('No header row to render the preview into')

  removePreviewCells(table)
  collapseReportColumns(table)

  const groups = flatten(columns)

  headerRow.appendChild(buildDateCells(groups))

  const valueRow = buildValueRow(headerRow, groups)
  if (valueRow) headerRow.insertAdjacentElement('afterend', valueRow)

  // Built once and cloned per row: a report can run to thousands of rows, and
  // each one needs the same blank cells.
  const bodyCells = buildBlankCells(groups)
  table
    .querySelectorAll<HTMLTableRowElement>('tbody tr')
    .forEach((row) => row.appendChild(bodyCells.cloneNode(true)))
}

const removePreviewCells = (table: HTMLTableElement): void => {
  disposeTooltips()

  table.querySelectorAll(`.${CELL_CLASS}`).forEach((cell) => cell.remove())
  table.querySelectorAll(`.${PREVIEW_ROW_CLASS}`).forEach((row) => row.remove())
}

// The row naming what each column shows, under the dates. The report renders one
// itself as soon as its own columns call for it (a budget set, adjustments, mixed
// relative periods) and leaves it out otherwise — but the columns being composed
// in the drawer are not the report's, and may need the row where the report's
// columns don't. So the preview always brings its own and the report's are hidden
// for as long as it is up (see reports.scss).
//
// None of that row's design is reproduced here. The report styles a subheader by
// where it sits — `.report thead tr:nth-child(n + 2)` — so a row in that position
// already looks the part, and the row and its label cell are shallow-cloned off
// the header row above, which carries the same classes `reports_helper` gives the
// report's own subheader row. Restyle either in Ruby and this follows.
const buildValueRow = (
  headerRow: HTMLTableRowElement,
  groups: PreviewGroup[]
): HTMLTableRowElement | null => {
  if (groups.every((group) => group.sublabels.length === 0)) return null

  const row = headerRow.cloneNode(false) as HTMLTableRowElement
  row.classList.add(PREVIEW_ROW_CLASS)

  // Attributes only, no children: the header row's label cell holds the report
  // name, the period-set picker and the collapse controls, and the subheader
  // row's is empty.
  row.appendChild(headerRow.cells[0].cloneNode(false))
  row.appendChild(buildValueCells(groups))

  return row
}

const disposeTooltips = (): void => {
  tooltips.forEach((tooltip) => tooltip.dispose())
  tooltips = []
}

// Flattens the configured columns into the periods the report would render for
// them: one configured column can span several periods (a year's worth of
// monthly periods is a period each), a spacer is a single blank column.
const flatten = (columns: PreviewColumn[]): PreviewGroup[] =>
  columns.flatMap((column): PreviewGroup[] =>
    column.spacer
      ? [{ label: '', sublabels: [], hidden: false, spacer: true }]
      : column.headers.map((header) => ({ ...header, spacer: false }))
  )

// How many columns the report renders for one period.
const columnCount = (group: PreviewGroup): number =>
  Math.max(1, group.sublabels.length)

// The dates. One cell per period, spanning the columns that period renders as.
const buildDateCells = (groups: PreviewGroup[]): DocumentFragment => {
  const fragment = document.createDocumentFragment()

  groups.forEach((group) => {
    fragment.appendChild(
      buildCell('th', group, {
        text: group.label,
        span: columnCount(group),
        explain: true,
      })
    )
  })

  return fragment
}

// The value row: one cell per column, naming what it shows. A period with nothing
// to say there still needs its cells, or the row would come up short and the
// columns after it would shift.
const buildValueCells = (groups: PreviewGroup[]): DocumentFragment => {
  const fragment = document.createDocumentFragment()

  groups.forEach((group) => {
    if (group.sublabels.length === 0) {
      fragment.appendChild(buildCell('th', group, {}))
      return
    }

    group.sublabels.forEach((sublabel) => {
      fragment.appendChild(buildCell('th', group, { text: sublabel }))
    })
  })

  return fragment
}

// A body row's worth: no figures are previewed, but every column needs its cell
// for the row to keep its shape.
const buildBlankCells = (groups: PreviewGroup[]): DocumentFragment => {
  const fragment = document.createDocumentFragment()

  groups.forEach((group) => {
    for (let index = 0; index < columnCount(group); index += 1) {
      fragment.appendChild(buildCell('td', group, {}))
    }
  })

  return fragment
}

// `explain` marks the cell that names the period: the one worth telling the user
// why the report won't show it.
const buildCell = (
  tag: 'th' | 'td',
  group: PreviewGroup,
  { text, span, explain }: { text?: string; span?: number; explain?: boolean }
): HTMLTableCellElement => {
  const cell = document.createElement(tag)
  cell.classList.add(CELL_CLASS, tag === 'th' ? HEADER_CLASS : BODY_CLASS)
  if (group.spacer) cell.classList.add(SPACER_CLASS)
  if (group.hidden) cell.classList.add(HIDDEN_CELL_CLASS)
  if (span && span > 1) cell.colSpan = span
  if (text) setMultilineText(cell, text)
  if (group.hidden && explain) explainHiddenPeriod(cell)

  return cell
}

const explainHiddenPeriod = (cell: HTMLElement): void => {
  const t = useColumnEditorI18n()
  const reason = t('hidden_period_tooltip')

  // The tooltip only answers a pointer. Greyed out is all the cell would say to a
  // screen reader otherwise, so the reason goes in it as text only that reads —
  // alongside the date rather than in place of it, which is what an `aria-label`
  // on the cell would do.
  const note = document.createElement('span')
  note.className = 'tw-sr-only'
  note.textContent = reason
  cell.appendChild(note)

  tooltips.push(
    new Tooltip(cell, {
      title: reason,
      placement: 'bottom',
      delay: 100,
    })
  )
}

// A difference column's header names both of its periods on separate lines.
const setMultilineText = (cell: HTMLElement, text: string): void => {
  text.split('\n').forEach((line, index) => {
    if (index > 0) cell.appendChild(document.createElement('br'))
    cell.appendChild(document.createTextNode(line))
  })
}
